import { queryClient } from "@/ajax/queryClient";
import Chat from "@/features/chat/Chat";
import { makeVirtualThread } from "@/features/chat/virtual-thread";
import { ORG_CHAT_THREADS_QUERY_KEY } from "@/features/chat/org-chat-threads-query-key";
import { useOrganization } from "@/hooks/use-organization";
import { useChatFormAccess } from "@/features/chat/hooks/use-chat-form-access";
import { useTRPC, useTRPCClient } from "@/trpc";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { useState, useMemo, useEffect, useCallback } from "react";
import type { User } from "@/common/types/user";

const PAGE_SIZE = 50;
const orgChatSearchSchema = z.object({
  thread: z.string().optional(),
  archived: z.boolean().optional(),
  assignedToMe: z.boolean().optional(),
  unreadOnly: z.boolean().optional(),
});

type OrgChatSearch = z.infer<typeof orgChatSearchSchema>;

function OrgChatPage({
  user,
  search,
}: {
  user: User | null;
  search: OrgChatSearch;
}) {
  const router = useRouter();
  const trpcClient = useTRPCClient();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { canManageJobPosts, documentSigningEnabled } = useChatFormAccess(user);

  // Memoize query parameters to prevent unnecessary re-renders
  const queryParams = useMemo(
    () => ({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      isArchived: search.archived === true,
      search: searchQuery || undefined,
      assignedToMe: search.assignedToMe === true ? true : undefined,
      unreadOnly: search.unreadOnly === true ? true : undefined,
    }),
    [
      selectedOrgBusinessUnit.id,
      search.archived,
      searchQuery,
      search.assignedToMe,
      search.unreadOnly,
    ],
  );

  const isSelectedVirtual = !!search.thread?.startsWith("virtual:");

  // Fetch selected thread if in URL on initial load (skip virtual threads)
  const { data: selectedThreadData, isLoading: isLoadingSelectedThread } =
    useQuery(
      trpc.chat.orgGetSingleThread.queryOptions(
        {
          threadId: search.thread!,
          organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          isArchived: search.archived === true,
        },
        {
          enabled: !!search.thread && !isSelectedVirtual,
        },
      ),
    );

  const selectedVirtualProviderUserId =
    isSelectedVirtual && search.thread
      ? search.thread.slice("virtual:".length)
      : undefined;

  // Fetch matching providers the org is allowed to start a chat with.
  const { data: assignedProviders, isLoading: isLoadingAssignedProviders } =
    useQuery(
      trpc.profileShare.listChatRecipients.queryOptions(
        {
          organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          searchTerm: searchQuery.trim() || undefined,
          providerUserId: selectedVirtualProviderUserId,
          limit: 25,
        },
        {
          enabled:
            search.unreadOnly !== true &&
            (!!searchQuery.trim() || !!selectedVirtualProviderUserId),
        },
      ),
    );

  // Use infinite query for pagination
  const {
    data: infiniteData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [ORG_CHAT_THREADS_QUERY_KEY, queryParams],
    queryFn: ({ pageParam = 0 }) =>
      trpcClient.chat.orgGetChatThreads.query({
        ...queryParams,
        offset: pageParam * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // If the response has pagination info and hasMore is true, return next page
      if (
        typeof lastPage === "object" &&
        "hasMore" in lastPage &&
        lastPage.hasMore
      ) {
        return allPages.length; // Next page index
      }
      // If it's an array (old format) and has 20 items, assume there might be more
      if (Array.isArray(lastPage) && lastPage.length === PAGE_SIZE) {
        return allPages.length; // Next page index
      }
      return undefined; // No more pages
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchInterval: searchQuery ? undefined : 30_000, // Only auto-refetch when not searching
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });

  // Flatten the paginated data and merge with selected thread only on initial load
  const realThreads = useMemo(() => {
    const pages =
      infiniteData?.pages.flatMap((page) => {
        // Handle both paginated format {threads: [...]} and old array format
        return Array.isArray(page) ? page : page.threads || [];
      }) || [];

    const selectedThreadMatchesUnreadFilter =
      search.unreadOnly !== true || (selectedThreadData?.unread_count ?? 0) > 0;

    if (
      selectedThreadData &&
      search.thread &&
      !isSelectedVirtual &&
      selectedThreadMatchesUnreadFilter
    ) {
      // Remove duplicate if thread is already in the list
      const hasThread = pages.find((t) => t.id === search.thread);
      if (!hasThread) {
        return [selectedThreadData, ...pages];
      }
    }

    return pages;
  }, [
    infiniteData,
    selectedThreadData,
    search.thread,
    search.unreadOnly,
    isSelectedVirtual,
  ]);

  // Build virtual threads for assigned providers matching the search query
  // who don't already have a real thread in the list
  const chatThreads = useMemo(() => {
    if (search.unreadOnly === true) {
      return realThreads;
    }

    const existingThreadUserIds = new Set(
      realThreads.flatMap((t) => t.user_participants.map((p) => p.id)),
    );

    const virtualThreads: ReturnType<typeof makeVirtualThread>[] = [];
    const query = searchQuery?.trim().toLowerCase() ?? "";

    if (query && assignedProviders) {
      for (const provider of assignedProviders) {
        if (existingThreadUserIds.has(provider.userId)) {
          continue;
        }
        virtualThreads.push(makeVirtualThread(provider));
      }
    }

    // If the URL has a virtual thread selected, always include it in the list
    // so the selected thread remains visible regardless of search state.
    if (isSelectedVirtual && search.thread && assignedProviders) {
      const userId = search.thread.slice("virtual:".length);
      const alreadyIncluded = virtualThreads.find(
        (t) => t.id === search.thread,
      );
      if (!alreadyIncluded && !existingThreadUserIds.has(userId)) {
        const provider = assignedProviders.find((p) => p.userId === userId);
        if (provider) {
          virtualThreads.unshift(makeVirtualThread(provider));
        }
      }
    }

    return [...realThreads, ...virtualThreads];
  }, [
    realThreads,
    assignedProviders,
    searchQuery,
    search.unreadOnly,
    isSelectedVirtual,
    search.thread,
  ]);

  const { data: myPoolAccess } = useQuery(
    trpc.organization.getMyProviderPoolAccess.queryOptions(
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      },
      {
        enabled: !!selectedOrgBusinessUnit.id,
      },
    ),
  );

  // Memoize the refresh function to prevent re-renders
  const handleRefreshThreads = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [ORG_CHAT_THREADS_QUERY_KEY],
    });
  }, []);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Chat
      user={user || undefined}
      chatThreads={chatThreads}
      organizationBusinessUnitId={selectedOrgBusinessUnit.id}
      canManageJobPosts={canManageJobPosts}
      documentSigningEnabled={documentSigningEnabled}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      onRefreshThreads={handleRefreshThreads}
      onLoadMore={handleLoadMore}
      hasMore={hasNextPage}
      isLoading={isLoading || isFetchingNextPage}
      isLoadingSelectedThread={
        isLoadingSelectedThread ||
        (isSelectedVirtual && isLoadingAssignedProviders)
      }
      assignedToMe={search.assignedToMe === true}
      onAssignedToMeChange={(value) => {
        router.navigate({
          // @ts-expect-error router search merge keeps existing route params
          search: (prev) => ({
            ...prev,
            assignedToMe: value || undefined,
          }),
        });
      }}
      unreadOnly={search.unreadOnly === true}
      onUnreadOnlyChange={(value) => {
        router.navigate({
          // @ts-expect-error router search merge keeps existing route params
          search: (prev) => {
            const { thread, ...rest } = prev;
            return {
              ...rest,
              unreadOnly: value || undefined,
            };
          },
        });
      }}
      isPoolRestricted={myPoolAccess?.restricted === true}
    />
  );
}

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/chat/",
)({
  component: OrgBusinessUnitChatRoute,
  validateSearch: orgChatSearchSchema,
});

function OrgBusinessUnitChatRoute() {
  const { user } = Route.useRouteContext();
  const search = Route.useSearch();
  return <OrgChatPage user={user} search={search} />;
}
